import { KeyRound, Mail, Pencil, Plus, Trash2, X } from 'lucide-react';
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';

const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400';

// Clinic types + labels. Keep in sync with backend/src/lib/clinic-type-presets.ts.
// Empty value lets the backend derive the type from the prospect specialty (or default to `other`).
export const CLINIC_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Auto (from prospect / default)' },
  { value: 'med_spa', label: 'Med Spa / Aesthetics' },
  { value: 'dermatology', label: 'Dermatology' },
  { value: 'physio', label: 'Physiotherapy / Rehab' },
  { value: 'gp', label: 'GP / Primary Care' },
  { value: 'wellness', label: 'Wellness / IV' },
  { value: 'iv_clinic', label: 'IV / Infusion' },
  { value: 'dental', label: 'Dental' },
  { value: 'radiology', label: 'Radiology / Imaging' },
  { value: 'other', label: 'Other / General' },
];

function formatDate(value?: string | null) {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'n/a';
  return date.toLocaleDateString();
}

function pillClass(value: string) {
  const normalized = value.toUpperCase();
  if (['ACTIVE', 'WON', 'SUBMITTED', 'TRIALING'].includes(normalized)) return 'pill pill-success';
  if (['PAST_DUE', 'QUESTIONNAIRE_SENT', 'QUESTIONNAIRE_SUBMITTED', 'QUALIFIED'].includes(normalized)) return 'pill pill-warn';
  if (['SUSPENDED', 'LOST', 'CHURNED', 'CANCELLED', 'EXPIRED'].includes(normalized)) return 'pill pill-danger';
  return 'pill pill-info';
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-2 block font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

function ModalDialog({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4">
      <div className="card max-h-[90vh] w-full max-w-4xl overflow-y-auto p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button type="button" className="rounded-lg border border-slate-200 p-2 text-slate-600" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function defaultCreateForm() {
  return {
    displayName: '',
    legalName: '',
    slug: '',
    clinicType: '',
    timezone: 'America/Toronto',
    billingEmail: '',
    billingContactName: '',
    billingCountry: '',
    planCode: 'enterprise',
    billingInterval: 'MONTHLY',
    basePrice: '0',
    overagePrice: '0',
    seatsIncluded: '10',
    trialEndsAt: '',
    notes: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminLanguage: 'en',
  };
}

export function OrganizationsPageV2() {
  const { fetchApi, fetchJson } = useApiClient();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [submittingCreate, setSubmittingCreate] = useState(false);
  const [form, setForm] = useState(defaultCreateForm());

  const load = () => {
    setLoading(true);
    fetchJson<{ data: any[] }>('/platform-admin/organizations')
      .then((payload) => setRows(payload.data))
      .catch((err) => setMessage(String(err?.message || 'Unable to load organizations.')))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [fetchJson]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((item) => item.name?.toLowerCase().includes(q) || item.tenantSlug?.toLowerCase().includes(q) || item.id?.toLowerCase().includes(q));
  }, [rows, search]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmittingCreate(true);
    setMessage(null);
    const res = await fetchApi('/platform-admin/tenants', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        displayName: form.displayName,
        legalName: form.legalName || form.displayName,
        slug: form.slug || undefined,
        clinicType: form.clinicType || undefined,
        timezone: form.timezone,
        billingEmail: form.billingEmail || undefined,
        billingContactName: form.billingContactName || undefined,
        billingCountry: form.billingCountry || undefined,
        planCode: form.planCode,
        billingInterval: form.billingInterval,
        basePrice: Number(form.basePrice || 0),
        overagePrice: Number(form.overagePrice || 0),
        seatsIncluded: Number(form.seatsIncluded || 10),
        trialEndsAt: form.trialEndsAt ? new Date(form.trialEndsAt).toISOString() : undefined,
        notes: form.notes || undefined,
        initialAdmin: {
          firstName: form.adminFirstName,
          lastName: form.adminLastName,
          email: form.adminEmail,
          preferredLanguage: form.adminLanguage,
        },
      }),
    });
    const payload = await res.json().catch(() => ({} as any));
    setSubmittingCreate(false);
    if (!res.ok) {
      setMessage(payload?.message || 'Unable to create organization.');
      return;
    }
    setShowCreateDialog(false);
    setForm(defaultCreateForm());
    setMessage(`Organization created. Invite mode: ${payload?.inviteDeliveryMode || 'unknown'}.`);
    load();
    if (payload?.organizationId) navigate(`/organizations/${payload.organizationId}`);
  };

  return (
    <div className="card p-5 lg:p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-slate-900">Organizations</h2>
        <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white" onClick={() => setShowCreateDialog(true)}>
          <Plus size={16} />
          Add organization
        </button>
      </div>
      <div className="mb-4">
        <input className={inputClass} placeholder="Search by name, slug, or id" value={search} onChange={(event) => setSearch(event.target.value)} />
      </div>
      {loading ? <div className="text-sm text-slate-500">Loading organizations...</div> : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-3 font-semibold">Organization</th>
                <th className="px-3 py-3 font-semibold">Tenant</th>
                <th className="px-3 py-3 font-semibold">Plan</th>
                <th className="px-3 py-3 font-semibold">Users</th>
                <th className="px-3 py-3 font-semibold">Locations</th>
                <th className="px-3 py-3 font-semibold">Patients</th>
                <th className="px-3 py-3 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((item) => (
                <tr key={item.id} className="cursor-pointer border-b border-slate-100 hover:bg-slate-50" onClick={() => navigate(`/organizations/${item.id}`)}>
                  <td className="px-3 py-3"><div className="font-semibold text-slate-900">{item.name}</div><div className="text-xs text-slate-500">{item.id}</div></td>
                  <td className="px-3 py-3"><div className="text-slate-700">{item.tenantSlug || 'not configured'}</div><div className="mt-1"><span className={pillClass(item.tenantStatus || 'DRAFT')}>{item.tenantStatus || 'DRAFT'}</span></div></td>
                  <td className="px-3 py-3 text-slate-700"><div>{item.planCode || 'n/a'}</div><div className="text-xs text-slate-500">{item.billingInterval || 'n/a'}</div></td>
                  <td className="px-3 py-3 text-slate-700">{item.userCount}</td>
                  <td className="px-3 py-3 text-slate-700">{item.locationCount}</td>
                  <td className="px-3 py-3 text-slate-700">{item.patientCount}</td>
                  <td className="px-3 py-3 text-slate-700">{formatDate(item.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {message ? <div className="mt-4 text-sm text-slate-600">{message}</div> : null}

      <ModalDialog open={showCreateDialog} title="Add organization" onClose={() => !submittingCreate && setShowCreateDialog(false)}>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={submit}>
          <Field label="Organization name"><input className={inputClass} value={form.displayName} onChange={(event) => setForm({ ...form, displayName: event.target.value })} required /></Field>
          <Field label="Legal name"><input className={inputClass} value={form.legalName} onChange={(event) => setForm({ ...form, legalName: event.target.value })} /></Field>
          <Field label="Tenant slug"><input className={inputClass} value={form.slug} onChange={(event) => setForm({ ...form, slug: event.target.value })} /></Field>
          <Field label="Clinic type"><select className={inputClass} value={form.clinicType} onChange={(event) => setForm({ ...form, clinicType: event.target.value })}>{CLINIC_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></Field>
          <Field label="Timezone"><input className={inputClass} value={form.timezone} onChange={(event) => setForm({ ...form, timezone: event.target.value })} required /></Field>
          <Field label="Billing email"><input className={inputClass} type="email" value={form.billingEmail} onChange={(event) => setForm({ ...form, billingEmail: event.target.value })} /></Field>
          <Field label="Billing contact"><input className={inputClass} value={form.billingContactName} onChange={(event) => setForm({ ...form, billingContactName: event.target.value })} /></Field>
          <Field label="Plan"><input className={inputClass} value={form.planCode} onChange={(event) => setForm({ ...form, planCode: event.target.value })} /></Field>
          <Field label="Billing interval"><select className={inputClass} value={form.billingInterval} onChange={(event) => setForm({ ...form, billingInterval: event.target.value })}><option value="MONTHLY">MONTHLY</option><option value="YEARLY">YEARLY</option></select></Field>
          <Field label="Admin first name"><input className={inputClass} value={form.adminFirstName} onChange={(event) => setForm({ ...form, adminFirstName: event.target.value })} required /></Field>
          <Field label="Admin last name"><input className={inputClass} value={form.adminLastName} onChange={(event) => setForm({ ...form, adminLastName: event.target.value })} required /></Field>
          <div className="md:col-span-2"><Field label="Admin email"><input className={inputClass} type="email" value={form.adminEmail} onChange={(event) => setForm({ ...form, adminEmail: event.target.value })} required /></Field></div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={() => setShowCreateDialog(false)} disabled={submittingCreate}>Cancel</button>
            <button className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white disabled:opacity-60" type="submit" disabled={submittingCreate}>{submittingCreate ? 'Creating...' : 'Create organization'}</button>
          </div>
        </form>
      </ModalDialog>
    </div>
  );
}

function formatCurrency(value: number, currency = 'USD') {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

// Grouped tenant feature flags. Keep in sync with backend tenant-feature-flags.ts.
const FEATURE_FLAG_GROUPS: Array<{ group: string; flags: Array<{ key: string; label: string }> }> = [
  {
    group: 'Core',
    flags: [
      { key: 'treatmentCatalogEnabled', label: 'Treatment catalog' },
      { key: 'populationHealthEnabled', label: 'Population health' },
      { key: 'publicInstitutionIntegrationsEnabled', label: 'Public institution integrations' },
    ],
  },
  {
    group: 'Radiology',
    flags: [
      { key: 'radiologyCoreEnabled', label: 'Radiology core' },
      { key: 'dicomIngestEnabled', label: 'DICOM ingest' },
      { key: 'pacsImportEnabled', label: 'PACS import' },
      { key: 'radiologyViewerEnabled', label: 'Radiology viewer' },
      { key: 'radiologyAnalyticsEnabled', label: 'Radiology analytics' },
      { key: 'radiologyCommsEnabled', label: 'Radiology comms' },
      { key: 'radiologyFhirSyncEnabled', label: 'Radiology FHIR sync' },
      { key: 'radiologyEdgeSupportEnabled', label: 'Radiology edge support' },
    ],
  },
  {
    group: 'AI',
    flags: [
      { key: 'aiTriageEnabled', label: 'AI triage' },
      { key: 'aiInterpretationEnabled', label: 'AI interpretation' },
      { key: 'aiReportDraftEnabled', label: 'AI report draft' },
      { key: 'aiExplainabilityEnabled', label: 'AI explainability' },
    ],
  },
  {
    group: 'FSE (Italy)',
    flags: [
      { key: 'fseCoreEnabled', label: 'FSE core' },
      { key: 'fseDocumentsEnabled', label: 'FSE documents' },
      { key: 'fsePatientDocumentAutoSyncEnabled', label: 'FSE patient doc auto-sync' },
      { key: 'fseFhirValidationEnabled', label: 'FSE FHIR validation' },
      { key: 'fseGatewayEnabled', label: 'FSE gateway' },
      { key: 'fseRegionalAdaptersEnabled', label: 'FSE regional adapters' },
    ],
  },
];

function FeatureFlagsCard({
  flags,
  clinicType,
  onToggle,
  savingKey,
}: {
  flags: Record<string, boolean>;
  clinicType: string | null;
  onToggle: (key: string, value: boolean) => void;
  savingKey: string | null;
}) {
  return (
    <div className="card p-5 lg:p-6">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Feature flags</h3>
        {clinicType ? <span className="text-xs text-slate-500">Clinic type: {clinicType}</span> : null}
      </div>
      <p className="mb-4 text-xs text-slate-500">Fine-tune individual features for this client (beyond the clinic-type preset). Changes save immediately.</p>
      <div className="space-y-5">
        {FEATURE_FLAG_GROUPS.map((grp) => (
          <div key={grp.group}>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{grp.group}</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {grp.flags.map((flag) => (
                <label key={flag.key} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                  <span className="text-slate-700">{flag.label}</span>
                  <input
                    type="checkbox"
                    checked={Boolean(flags?.[flag.key])}
                    disabled={savingKey === flag.key}
                    onChange={(event) => onToggle(flag.key, event.target.checked)}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function OrganizationDetailsPageV2() {
  const { organizationId = '' } = useParams();
  const navigate = useNavigate();
  const { fetchApi, fetchJson } = useApiClient();
  const [details, setDetails] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', firstName: '', lastName: '', roleId: '' });
  const [editForm, setEditForm] = useState({ id: '', firstName: '', lastName: '', roleId: '', active: true });
  const [savingFlag, setSavingFlag] = useState<string | null>(null);

  const toggleFlag = async (key: string, value: boolean) => {
    if (!organizationId) return;
    setSavingFlag(key);
    const res = await fetchApi(`/platform-admin/organizations/${organizationId}/feature-flags`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [key]: value }),
    });
    const payload = await res.json().catch(() => ({} as any));
    setSavingFlag(null);
    if (!res.ok) {
      setMessage(payload?.message || 'Unable to update feature flag.');
      return;
    }
    setDetails((current: any) =>
      current ? { ...current, featureFlags: payload?.data?.featureFlags ?? current.featureFlags } : current,
    );
  };

  const load = () => {
    if (!organizationId) return;
    setLoading(true);
    Promise.all([
      fetchJson<{ data: any }>(`/platform-admin/organizations/${organizationId}`),
      fetchJson<{ data: any[]; roles: any[] }>(`/platform-admin/organizations/${organizationId}/users`),
    ])
      .then(([orgPayload, usersPayload]) => {
        setDetails(orgPayload.data);
        setUsers(usersPayload.data);
        setRoles(usersPayload.roles || []);
        if (!addForm.roleId && usersPayload.roles?.[0]) {
          setAddForm((current) => ({ ...current, roleId: String(usersPayload.roles[0].id) }));
        }
        setError(null);
      })
      .catch((err) => {
        setError(String(err?.message || 'Unable to load organization details.'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [organizationId, fetchJson]);

  const createUser = async (event: FormEvent) => {
    event.preventDefault();
    if (!organizationId) return;
    setBusy(true);
    const res = await fetchApi(`/platform-admin/organizations/${organizationId}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: addForm.email,
        firstName: addForm.firstName,
        lastName: addForm.lastName,
        roleId: Number(addForm.roleId),
      }),
    });
    const payload = await res.json().catch(() => ({} as any));
    setBusy(false);
    if (!res.ok) return setMessage(payload?.message || 'Unable to create user.');
    setShowAdd(false);
    setAddForm({ email: '', firstName: '', lastName: '', roleId: roles[0] ? String(roles[0].id) : '' });
    setMessage(`User created. Temporary password: ${payload?.data?.temporaryPassword || 'not returned'}.`);
    load();
  };

  const saveUser = async (event: FormEvent) => {
    event.preventDefault();
    if (!organizationId || !editForm.id) return;
    setBusy(true);
    const res = await fetchApi(`/platform-admin/organizations/${organizationId}/users/${editForm.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        roleId: Number(editForm.roleId),
        active: editForm.active,
      }),
    });
    const payload = await res.json().catch(() => ({} as any));
    setBusy(false);
    if (!res.ok) return setMessage(payload?.message || 'Unable to update user.');
    setShowEdit(false);
    setMessage('User updated.');
    load();
  };

  const deleteUser = async (user: any) => {
    if (!organizationId) return;
    if (!window.confirm(`Delete ${user.firstName} ${user.lastName} (${user.email})?`)) return;
    const res = await fetchApi(`/platform-admin/organizations/${organizationId}/users/${user.id}`, { method: 'DELETE' });
    const payload = await res.json().catch(() => ({} as any));
    if (!res.ok) return setMessage(payload?.message || 'Unable to delete user.');
    setMessage('User deleted.');
    load();
  };

  const resetPassword = async (user: any) => {
    if (!organizationId) return;
    const res = await fetchApi(`/platform-admin/organizations/${organizationId}/users/${user.id}/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const payload = await res.json().catch(() => ({} as any));
    if (!res.ok) return setMessage(payload?.message || 'Unable to reset password.');
    setMessage(
      `Password reset for ${user.email}. Temporary password: ${payload?.data?.temporaryPassword || 'not returned'}. Email delivery: ${payload?.data?.emailDeliveryMode || 'unknown'}.`,
    );
    load();
  };

  const resendInvite = async (user: any) => {
    if (!organizationId) return;
    const res = await fetchApi(`/platform-admin/organizations/${organizationId}/users/${user.id}/resend-invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const payload = await res.json().catch(() => ({} as any));
    if (!res.ok) return setMessage(payload?.message || 'Unable to resend invite.');
    setMessage(
      `Invite resent to ${user.email}. Temporary password: ${payload?.data?.temporaryPassword || 'not returned'}. Email delivery: ${payload?.data?.inviteDeliveryMode || 'unknown'}.`,
    );
    load();
  };

  if (loading) return <div className="card p-5 text-sm text-slate-500">Loading organization details...</div>;
  if (error || !details) return <div className="card p-5 text-sm text-rose-600">{error || 'Organization not found.'}</div>;

  return (
    <div className="space-y-6">
      <div className="card p-5 lg:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-slate-900">{details.name}</h2>
          <button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={() => navigate('/organizations')}>Back to organizations</button>
        </div>
        <div className="grid gap-4 md:grid-cols-3 text-sm">
          <div><div className="text-slate-500">Timezone</div><div className="font-semibold text-slate-900">{details.timezone}</div></div>
          <div><div className="text-slate-500">Users / Locations / Patients</div><div className="font-semibold text-slate-900">{details.userCount} / {details.locationCount} / {details.patientCount}</div></div>
          <div><div className="text-slate-500">Created</div><div className="font-semibold text-slate-900">{formatDate(details.createdAt)}</div></div>
        </div>
      </div>

      {details.featureFlags ? (
        <FeatureFlagsCard
          flags={details.featureFlags}
          clinicType={details.clinicType ?? null}
          onToggle={toggleFlag}
          savingKey={savingFlag}
        />
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        <div className="card p-5 lg:p-6">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">Tenant details</h3>
          {details.tenantAccount ? (
            <div className="space-y-2 text-sm text-slate-700">
              <div>Slug: <strong>{details.tenantAccount.slug}</strong></div>
              <div>Status: <span className={pillClass(details.tenantAccount.status)}>{details.tenantAccount.status}</span></div>
              <div>Plan: {details.tenantAccount.planCode} ({details.tenantAccount.billingInterval})</div>
              <div>Billing contact: {details.tenantAccount.billingContactName || 'n/a'}</div>
              <div>Billing email: {details.tenantAccount.billingEmail || 'n/a'}</div>
            </div>
          ) : <div className="text-sm text-slate-500">Tenant account is not configured for this organization.</div>}
        </div>
        <div className="card p-5 lg:p-6">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">Billing information</h3>
          {details.subscription ? (
            <div className="grid gap-3 md:grid-cols-2 text-sm text-slate-700">
              <div>Subscription: <span className={pillClass(details.subscription.status)}>{details.subscription.status}</span></div>
              <div>Plan: {details.subscription.planCode}</div>
              <div>Base price: {formatCurrency(details.subscription.basePrice, details.subscription.currency)}</div>
              <div>Balance: {formatCurrency(details.subscription.balance, details.subscription.currency)}</div>
              <div>Seats: {details.subscription.activeSeats} / {details.subscription.seatsIncluded}</div>
              <div>Next invoice: {formatDate(details.subscription.nextInvoiceDate)}</div>
            </div>
          ) : <div className="text-sm text-slate-500">Billing is not configured for this organization yet.</div>}
        </div>
      </div>

      <div className="card p-5 lg:p-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-900">Users</h3>
          <button type="button" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white" onClick={() => setShowAdd(true)}><Plus size={16} />Add user</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-3 font-semibold">User</th><th className="px-3 py-3 font-semibold">Role</th><th className="px-3 py-3 font-semibold">Status</th><th className="px-3 py-3 font-semibold">Joined</th><th className="px-3 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-slate-100">
                  <td className="px-3 py-3"><div className="font-semibold text-slate-900">{user.firstName} {user.lastName}</div><div className="text-xs text-slate-500">{user.email}</div></td>
                  <td className="px-3 py-3 text-slate-700">{user.roleName}</td>
                  <td className="px-3 py-3"><span className={pillClass(user.active ? 'ACTIVE' : 'SUSPENDED')}>{user.active ? 'ACTIVE' : 'INACTIVE'}</span></td>
                  <td className="px-3 py-3 text-slate-700">{formatDate(user.createdAt)}</td>
                  <td className="px-3 py-3"><div className="flex justify-end gap-2">
                    <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700" onClick={() => { setEditForm({ id: user.id, firstName: user.firstName, lastName: user.lastName, roleId: String(user.roleId), active: user.active }); setShowEdit(true); }}><Pencil size={13} />Edit</button>
                    <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700" onClick={() => resendInvite(user)}><Mail size={13} />Invite</button>
                    <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700" onClick={() => resetPassword(user)}><KeyRound size={13} />Reset</button>
                    <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2 py-1 text-xs text-rose-700" onClick={() => deleteUser(user)}><Trash2 size={13} />Delete</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {message ? <div className="mt-4 text-sm text-slate-600">{message}</div> : null}
      </div>

      <ModalDialog open={showAdd} title="Add user" onClose={() => !busy && setShowAdd(false)}>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={createUser}>
          <Field label="First name"><input className={inputClass} value={addForm.firstName} onChange={(event) => setAddForm({ ...addForm, firstName: event.target.value })} required /></Field>
          <Field label="Last name"><input className={inputClass} value={addForm.lastName} onChange={(event) => setAddForm({ ...addForm, lastName: event.target.value })} required /></Field>
          <div className="md:col-span-2"><Field label="Email"><input className={inputClass} type="email" value={addForm.email} onChange={(event) => setAddForm({ ...addForm, email: event.target.value })} required /></Field></div>
          <div className="md:col-span-2"><Field label="Role"><select className={inputClass} value={addForm.roleId} onChange={(event) => setAddForm({ ...addForm, roleId: event.target.value })} required>{roles.map((role) => <option key={role.id} value={String(role.id)}>{role.name}</option>)}</select></Field></div>
          <div className="md:col-span-2 flex justify-end gap-2"><button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={() => setShowAdd(false)} disabled={busy}>Cancel</button><button className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white disabled:opacity-60" type="submit" disabled={busy}>{busy ? 'Creating...' : 'Create user'}</button></div>
        </form>
      </ModalDialog>

      <ModalDialog open={showEdit} title="Edit user" onClose={() => !busy && setShowEdit(false)}>
        <form className="grid gap-4 md:grid-cols-2" onSubmit={saveUser}>
          <Field label="First name"><input className={inputClass} value={editForm.firstName} onChange={(event) => setEditForm({ ...editForm, firstName: event.target.value })} required /></Field>
          <Field label="Last name"><input className={inputClass} value={editForm.lastName} onChange={(event) => setEditForm({ ...editForm, lastName: event.target.value })} required /></Field>
          <Field label="Role"><select className={inputClass} value={editForm.roleId} onChange={(event) => setEditForm({ ...editForm, roleId: event.target.value })} required>{roles.map((role) => <option key={role.id} value={String(role.id)}>{role.name}</option>)}</select></Field>
          <Field label="Status"><select className={inputClass} value={editForm.active ? 'active' : 'inactive'} onChange={(event) => setEditForm({ ...editForm, active: event.target.value === 'active' })}><option value="active">Active</option><option value="inactive">Inactive</option></select></Field>
          <div className="md:col-span-2 flex justify-end gap-2"><button type="button" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700" onClick={() => setShowEdit(false)} disabled={busy}>Cancel</button><button className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white disabled:opacity-60" type="submit" disabled={busy}>{busy ? 'Saving...' : 'Save user'}</button></div>
        </form>
      </ModalDialog>
    </div>
  );
}
